import {toBaseScalarPricing, toUniformCurrencyRows} from "@/features/pricing";
import type {SubscriptionTierFormValues} from "../../components/offering-form/subscriptions/subscription-tier-form/types";
import type {AddSubscriptionTierItemPayload} from "../../react-query-hooks/store/subscriptions/tiers/types";
import type {CreateSubscriptionApiTier} from "../../react-query-hooks/store/subscriptions/create-subscription/types";
import {
  getBillingDuration,
  getBillingInterval,
} from "../../react-query-hooks/store/subscriptions/constants/billing-enums";

/** Maps create/edit tier form rows to POST/PATCH tier API payloads. */
export function tierFormRowToAddTierPayload(
  tier: SubscriptionTierFormValues,
  sortOrder: number
): AddSubscriptionTierItemPayload {
  const firstCurrency = tier.currencies?.[0];
  const frequency = tier.billingFrequency ?? "monthly";
  const isFreeTier = tier.priceType === "free";
  const redirectType = !tier.redirectAfterPurchaseEnabled
    ? "none"
    : tier.redirectAfterPurchase === "custom-url"
    ? "url"
    : tier.redirectAfterPurchase ?? "none";
  const isPayWhatYouWant = tier.priceType === "pwyw";
  // A tier sends its base currency twice: as the defaults and again as row one. Under
  // Pay What You Want each row carries its Minimum Price, read the way every offering
  // reads it so that an unentered minimum is left out rather than sent as zero.
  const pwywPricing = isPayWhatYouWant
    ? toBaseScalarPricing(tier.currencies ?? [])
    : null;
  const defaultCurrency =
    pwywPricing?.callPriceCurrency ?? firstCurrency?.callPriceCurrency ?? "USD";
  const currencies = isFreeTier
    ? [{callPriceCurrency: defaultCurrency, callPrice: 0}]
    : pwywPricing
    ? toUniformCurrencyRows(pwywPricing).map((row) => ({
        ...row,
        callPrice: row.callPrice ?? 0,
      }))
    : (tier.currencies ?? []).map((c) => ({
        callPriceCurrency: c.callPriceCurrency,
        callPrice: Number(c.callPrice) || 0,
      }));
  const payload: AddSubscriptionTierItemPayload = {
    name: (tier.tierName ?? "").trim(),
    billingInterval: getBillingInterval(frequency),
    billingDuration: getBillingDuration(frequency),
    defaultPrice: isFreeTier
      ? 0
      : firstCurrency
      ? Number(firstCurrency.callPrice) || 0
      : 0,
    defaultCurrency,
    isPayWhatYouWant,
    ...(pwywPricing?.minCallPrice === undefined
      ? {}
      : {defaultMinPrice: pwywPricing.minCallPrice}),
    features: (tier.features ?? []).map((f) => f.value).filter(Boolean),
    sortOrder,
    currencies,
    ...(redirectType === "telegram"
      ? {
          redirectType,
          telegramGroupChatId: tier.telegramGroupId ?? null,
          urlRedirect: null,
        }
      : redirectType === "url"
      ? {redirectType, telegramGroupChatId: null, urlRedirect: tier.urlRedirect ?? null}
      : {}),
  };
  if (tier.billingCount === "custom") {
    payload.billingCycles = Number(tier.billingCycles) || 1;
  } else {
    payload.billingCycles = null;
  }
  return payload;
}

export function addTierPayloadToCreateApiTier(
  payload: AddSubscriptionTierItemPayload
): CreateSubscriptionApiTier {
  return {
    name: payload.name,
    billingInterval: payload.billingInterval,
    billingDuration: payload.billingDuration,
    billingCycles: payload.billingCycles,
    defaultPrice: payload.defaultPrice,
    defaultCurrency: payload.defaultCurrency,
    isPayWhatYouWant: payload.isPayWhatYouWant,
    ...(payload.defaultMinPrice === undefined
      ? {}
      : {defaultMinPrice: payload.defaultMinPrice}),
    features: payload.features,
    sortOrder: payload.sortOrder,
    currencies: payload.currencies ?? [],
    ...(payload.redirectType
      ? {
          redirectType: payload.redirectType,
          telegramGroupChatId: payload.telegramGroupChatId,
          urlRedirect: payload.urlRedirect,
        }
      : {}),
  };
}
